import { createContext, useContext, useMemo } from "react";

import type { AppBrandingDto } from "@/api/types";
import { Api } from "@/api/types";
import { addToHeaders } from "@/helpers/headers";
import { useConfig } from "@/providers/config/useConfig";

export const ProjectHeaderName = "X-ProjectId";
export const ContentLanguageHeaderName = "X-Content-Language";

interface ApiScope {
  projectId?: string;
  token?: string;
  appBranding?: AppBrandingDto;
}

type ApiClientContextValue = ApiScope & { api: Api<null> };
const ApiClientContext = createContext<ApiClientContextValue | null>(null);

export function useApiClient(): ApiClientContextValue {
  const context = useContext(ApiClientContext);
  if (!context) {
    throw new Error("useApiClient must be used within an ApiClientProvider");
  }

  return context;
}

/** Supplies an immutable request scope. Nested scopes inherit credentials/branding, never a mutable current project. */
export function ApiClientProvider({ children, ...scope }: React.PropsWithChildren<ApiScope>): React.ReactNode {
  const parent = useContext(ApiClientContext);
  const baseUrl = useConfig("newCoreApiRootUri");
  const projectId = scope.projectId;
  const token = scope.token ?? parent?.token;
  const appBranding = scope.appBranding ?? parent?.appBranding;

  // Query functions retain this client across retries. Its headers must keep the scope
  // in which the function was created, even after navigation changes the current scope.
  const api = useMemo(() => {
    if (!baseUrl) {
      throw new Error("No API base URL configured");
    }

    return new Api<null>({
      baseUrl,
      customFetch: async (input, init) => {
        init = addToHeaders(init, ProjectHeaderName, projectId);
        init = addToHeaders(init, "Client-Info", appBranding ? `platform=web;appbrand=${appBranding}` : "platform=web");
        init = addToHeaders(init, "Authorization", token ? `Bearer ${token}` : undefined);

        // The generated client throws this response on failure. Keep its request context for error reporting.
        const response = await fetch(input, init);

        return Object.assign(response, { requestMethod: init.method ?? "GET", requestBaseUrl: baseUrl });
      },
    });
  }, [baseUrl, projectId, token, appBranding]);

  return <ApiClientContext value={{ api, projectId, token, appBranding }}>{children}</ApiClientContext>;
}
